import React from "react";
import PropTypes from "prop-types";
import CheckIcon from "@mui/icons-material/Check";
import RemoveIcon from "@mui/icons-material/Remove";

import UICheckbox from "../../../../components/ui/UICheckbox";
import {
  ALL_MODULES,
  PERMISSION_GROUPS,
  PERMISSION_LEVELS,
  enabledLevelsFor,
  isLevelEnabled,
} from "./permissionModules";
import { applyPermissionDependencies } from "./permissionDependencies";

/** Tri-state for a set of boolean flags. */
const groupState = (flags) => {
  const all = flags.length > 0 && flags.every(Boolean);
  const some = flags.some(Boolean);
  return { checked: all, indeterminate: some && !all };
};

const findModule = (moduleName) =>
  ALL_MODULES.find((m) => m.moduleName === moduleName);

const PermissionsMatrix = ({ value, onChange, copy, readOnly }) => {
  const { effective, forced } = applyPermissionDependencies(value);

  const setModuleFlag = (moduleName, flag, checked) => {
    onChange({
      ...value,
      [moduleName]: { ...value[moduleName], [flag]: checked },
    });
  };

  const setModuleAll = (moduleName, checked) => {
    const module = findModule(moduleName);
    const next = enabledLevelsFor(module).reduce(
      (acc, level) => ({ ...acc, [level.flag]: checked }),
      { ...value[moduleName] },
    );
    onChange({ ...value, [moduleName]: next });
  };

  const setColumnAll = (flag, checked) => {
    const next = { ...value };
    ALL_MODULES.forEach((module) => {
      if (!isLevelEnabled(module, flag)) return;
      next[module.moduleName] = {
        ...next[module.moduleName],
        [flag]: checked,
      };
    });
    onChange(next);
  };

  const moduleTriState = (module) =>
    groupState(
      enabledLevelsFor(module).map(
        (level) => effective[module.moduleName][level.flag],
      ),
    );

  const columnTriState = (flag) =>
    groupState(
      ALL_MODULES.filter((module) => isLevelEnabled(module, flag)).map(
        (module) => effective[module.moduleName][flag],
      ),
    );

  return (
    <div className="create-role__matrix">
      <div className="create-role__matrix-row create-role__matrix-row--header">
        <div className="create-role__matrix-module-col create-role__matrix-module-col--header">
          {copy.modulesHeader}
        </div>
        {PERMISSION_LEVELS.map((level) => {
          if (readOnly) {
            return (
              <div
                className="create-role__matrix-cell create-role__matrix-cell--header"
                key={level.key}
              >
                <span className="create-role__matrix-col-label">
                  {copy.levels[level.key]}
                </span>
              </div>
            );
          }
          const state = columnTriState(level.flag);
          return (
            <div
              className="create-role__matrix-cell create-role__matrix-cell--header"
              key={level.key}
            >
              <UICheckbox
                label=""
                checked={state.checked}
                indeterminate={state.indeterminate}
                onChange={(checked) => setColumnAll(level.flag, checked)}
              />
              <span className="create-role__matrix-col-label">
                {copy.levels[level.key]}
              </span>
            </div>
          );
        })}
      </div>

      {PERMISSION_GROUPS.map((group) => (
        <div className="create-role__matrix-group" key={group.key}>
          <div className="create-role__matrix-group-header">
            <span
              className="create-role__matrix-group-name"
              style={{ color: group.color }}
            >
              {copy.groups[group.key]}
            </span>
            <span
              className="create-role__matrix-group-count"
              style={{ background: `${group.color}14`, color: group.color }}
            >
              {copy.moduleCount.replace("{count}", group.modules.length)}
            </span>
          </div>

          {group.modules.map((module) => {
            const state = moduleTriState(module);
            const rowDisabled = enabledLevelsFor(module).length === 0;
            const stripe = ALL_MODULES.indexOf(module) % 2 === 0;
            return (
              <div
                className={`create-role__matrix-row${
                  stripe ? " create-role__matrix-row--stripe" : ""
                }`}
                key={module.key}
              >
                <div className="create-role__matrix-module-col">
                  {!readOnly && (
                    <UICheckbox
                      label=""
                      checked={state.checked}
                      indeterminate={state.indeterminate}
                      disabled={rowDisabled}
                      onChange={(checked) =>
                        setModuleAll(module.moduleName, checked)
                      }
                    />
                  )}
                  <span className="create-role__matrix-module-name">
                    {copy.modules[module.key]}
                  </span>
                </div>
                {PERMISSION_LEVELS.map((level) => {
                  const disabled = !isLevelEnabled(module, level.flag);
                  const isForced = Boolean(
                    forced[module.moduleName][level.flag],
                  );
                  if (readOnly) {
                    const active = Boolean(
                      effective[module.moduleName][level.flag],
                    );
                    return (
                      <div
                        className="create-role__matrix-cell create-role__matrix-cell--readonly"
                        key={level.key}
                      >
                        {active ? (
                          <CheckIcon className="create-role__matrix-tick" />
                        ) : (
                          <RemoveIcon className="create-role__matrix-dash" />
                        )}
                      </div>
                    );
                  }
                  const cell = (
                    <div
                      className={`create-role__matrix-cell${
                        isForced ? " create-role__matrix-cell--locked" : ""
                      }`}
                      key={level.key}
                    >
                      <UICheckbox
                        label=""
                        checked={effective[module.moduleName][level.flag]}
                        disabled={disabled || isForced}
                        onChange={(checked) =>
                          setModuleFlag(module.moduleName, level.flag, checked)
                        }
                      />
                    </div>
                  );
                  return isForced ? (
                    <span
                      key={level.key}
                      title={copy.lockedHint}
                      className="create-role__matrix-cell-wrap"
                    >
                      {cell}
                    </span>
                  ) : (
                    cell
                  );
                })}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

PermissionsMatrix.propTypes = {
  value: PropTypes.objectOf(
    PropTypes.shape({
      can_view: PropTypes.bool,
      can_add: PropTypes.bool,
      can_edit: PropTypes.bool,
      can_delete: PropTypes.bool,
    }),
  ).isRequired,
  onChange: PropTypes.func,
  copy: PropTypes.shape({
    modulesHeader: PropTypes.string.isRequired,
    moduleCount: PropTypes.string.isRequired,
    lockedHint: PropTypes.string.isRequired,
    levels: PropTypes.object.isRequired,
    groups: PropTypes.object.isRequired,
    modules: PropTypes.object.isRequired,
  }).isRequired,
  readOnly: PropTypes.bool,
};

PermissionsMatrix.defaultProps = {
  onChange: () => {},
  readOnly: false,
};

export default PermissionsMatrix;
